import { useEffect, Fragment } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Badge } from 'react-bootstrap';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { formatNumber, sortTableDateFechaInicio, sortTableDateFechaFin, sortTableStatusStatus } from '../../utils/generalFunctions';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import { setAlert } from '../../actions/alert';
import { obtenerCliente, limpiarCliente } from '../../actions/clientes';

import NuevoPaquete from './NuevoPaquete';
import EditarPaquete from './EditarPaquete';
import EliminarPaquete from './EliminarPaquete';
import GestionarMembresia from './GestionarMembresia';
import NuevoPago from './NuevoPago';
import EliminarPago from './EliminarPago';

const DetallesCliente = () => {

    let rutasBread = [
        {
            activo: false,
            nombre: 'Clientes',
            path: '/clientes'
        },
        {
            activo: true,
            nombre: 'Perfil del cliente',
            path: '/clientes/detalles/'
        }
    ];

    const dispatch = useDispatch();

    const { id } = useParams();

    const { usuario } = useSelector(state => state.auth);
    const { detalles } = useSelector(state => state.clientes);

    const [clienteData, setClienteData] = useState({
        indicadores_generales: {
            citas_pendientes: 0,
            citas_realizadas: 0,
            citas_canceladas: 0,
            membresia_actual: null,
            saldo: 0
        },
        informacion_personal: {
            _id: null,
            nombre: null,
            nacimiento: null,
            celular: null,
            email: null,
            creacion: null,
            status: false
        },
        citas: [],
        paquetes: [],
        membresias: [],
        pagos: []
    });

    const [membresiaActual, setMembresiaActual] = useState(null);

    /* Paquetes */
    const [nuevoPaquete, setNuevoPaquete] = useState({
        cliente: null
    });
    const [editarPaquete, setEditarPaquete] = useState(null);
    const [eliminarPaquete, setEliminarPaquete] = useState(null);

    /* Pagos */
    const [nuevoPago, setNuevoPago] = useState(null);
    const [eliminarPagoData, setEliminarPagoData] = useState(null);

    const {indicadores_generales, informacion_personal, citas, paquetes, membresias, pagos} = clienteData;

    const paginationOptions = { rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' };

    const citasColumnas = [
        {
            name: 'Locatario',
            sortable: true, 
            wrap: true, 
            selector: row => row.locatario ? (row.locatario.servicio ? row.locatario.servicio?.nombre + ' (' + row.locatario.nombre + ')' : row.locatario.nombre) : 'Sin definir', 
            grow: 3
        },
        {
            name: 'Fecha de inicio',
            sortable: true, 
            sortFunction: sortTableDateFechaInicio, 
            cell: item => new Date(item.fecha_inicio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) + ' a las ' + new Date(item.fecha_inicio).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }), 
            grow: 2
        },
        {
            name: 'Fecha de finalización',
            sortable: true, 
            sortFunction: sortTableDateFechaFin, 
            cell: item => new Date(item.fecha_fin).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) + ' a las ' + new Date(item.fecha_fin).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }), 
            grow: 2
        },
        {
            name: 'Status',
            sortable: true, 
            sortFunction: sortTableStatusStatus, 
            selector: row => {
                switch(row.status){
                    case 0:
                        return(
                            <h5><Badge pill bg="warning" className="text-dark">Agendada</Badge></h5>
                        )
                    case 1:
                        return(
                            <h5><Badge pill bg="success">{row.tipo == 0 ? 'Finalizada' : 'Autorizado'}</Badge></h5>
                        )
                    case 2:
                        return(
                            <h5><Badge pill bg="danger">{'Cancelad' + (row.tipo == 0 ? 'a' : 'o')}</Badge></h5>
                        )
                    default:
                        break;
                }
            }
        }
    ];

    const paquetesColumnas = [
        {
            name: 'Paquete',
            sortable: true, 
            selector: row => row.paquete?.nombre || 'Sin definir',
            grow: 2
        },
        {
            name: 'Total',
            selector: row => formatNumber(row.total, 2, '$ ', '')
        },
        {
            name: 'Método de pago',
            sortable: true, 
            selector: row => {
                switch(row.metodo){
                    case 0:
                        return 'Efectivo';
                    case 1:
                        return 'Tarjeta';
                    case 2:
                        return 'Transferencia';
                    default:
                        break;
                }
            },
        }, 
        {
            name: 'Fecha de compra',
            sortable: true, 
            cell: item => new Date(item.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) + ' a las ' + new Date(item.fecha).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }), 
            grow: 2
        },
        {
            name: 'Citas totales',
            selector: row => formatNumber(row.citas_totales, 0, '', '')
        }, 
        {
            name: 'Citas disponibles',
            selector: row => formatNumber(row.citas_disponibles, 0, '', '')
        }, 
        {
            name: '',
            grow: 1, 
            cell: row => {
                return(
                    <div className="text-right w-100">
                        <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEliminarPaquete({...row, id_cliente: id})}><i className="fas fa-times text-danger"></i></button>
                        <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => handleEditarPaquete({...row, id_cliente: id})}><i className="fas fa-edit text-primary"></i></button>
                    </div>
                )
            }
        }
    ];

    const membresiasColumnas = [
        {
            name: 'Membresía',
            sortable: true, 
            selector: row => row.membresia?.nombre || 'Sin definir',
        },
        {
            name: 'Total',
            selector: row => formatNumber(row.total, 2, '$ ', '')
        },
        {
            name: 'Método de pago',
            sortable: true, 
            selector: row => {
                switch(row.metodo){
                    case 0:
                        return 'Efectivo';
                    case 1:
                        return 'Tarjeta';
                    case 2:
                        return 'Transferencia';
                    default:
                        break;
                }
            },
        }, 
        {
            name: 'Periodo',
            sortable: true, 
            selector: row => handleRangoFechas(row.fecha_inicio, row.fecha_fin),
            grow: 3
        },
        {
            name: 'Status',
            sortable: true, 
            sortFunction: sortTableStatusStatus, 
            selector: row => {
                switch(row.status){
                    case 0:
                        return(
                            <h5><Badge pill bg="success">Activa</Badge></h5>
                        )
                    case 1:
                        return(
                            <h5><Badge pill bg="warning">Vencida</Badge></h5>
                        )
                    case 2:
                        return(
                            <h5><Badge pill bg="danger">Cancelada</Badge></h5>
                        )
                    default:
                        break;
                }
            },
        }
    ];

    const beneficiosColumnas = [
        {
            name: 'Beneficio',
            sortable: true,
            selector: row => row,
        }
    ];

    const pagosColumnas = [
        {
            name: 'Fecha',
            sortable: true,
            cell: item => new Date(item.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }),
            grow: 2
        },
        {
            name: 'Concepto',
            selector: row => row.concepto,
            grow: 3
        },
        {
            name: 'Monto',
            sortable: true,
            cell: row => (
                <span style={{ color: row.monto >= 0 ? '#198754' : '#dc3545', fontWeight: 600 }}>
                    {row.monto >= 0 ? '+' : ''}{formatNumber(row.monto, 2, '$ ', '')}
                </span>
            )
        },
        {
            name: '',
            cell: row => row.cita == null ? (
                <div className="text-right w-100">
                    <button type="button" className="btn btn-sm btn-link px-2 float-end" onClick={() => setEliminarPagoData({ ...row, id_cliente: id })}><i className="fas fa-times text-danger"></i></button>
                </div>
            ) : null
        }
    ];

    /* Pagos */
    const handleAgregarPago = (cliente) => {
        setNuevoPago({ cliente });
    }

    /* Paquetes */
    const handleAgregarPaquete = (cliente) => {
        setNuevoPaquete({
            cliente
        });
    }

    const handleEditarPaquete = (paquete) => {
        setEditarPaquete(paquete);
    }

    const handleEliminarPaquete = (paquete) => {
        if(paquete?.citas_totales == paquete?.citas_disponibles){
            setEliminarPaquete(paquete);
        }else{
            dispatch(setAlert('No se puede eliminar porque ya se usaron citas', 'danger'));
        }
    }

    /* Funciones generales */
    const handleRangoFechas = (inicio, fin) => {
        let diferencia = new Date(fin).getTime() - new Date(inicio).getTime();
        let total_dias = Math.ceil(diferencia / (1000 * 3600 * 24));

        if(total_dias <= 1){
            return 'Unicamente el ' + (new Date(inicio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }))
        }else{
            return 'Del ' + (new Date(inicio).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })) + ' al ' + (new Date(fin).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }))
        }
    }

    const handleStatus = (membresia) => {
        switch(membresia?.status){
            case 0:
                return(
                    <h3 className="text-muted"><Badge pill bg="success">{membresia?.nombre}</Badge></h3>
                )
            case 1:
                return(
                    <h3 className="text-muted"><Badge pill bg="warning">Vencida</Badge></h3>
                )
            case 2:
                return(
                    <h3 className="text-muted"><Badge pill bg="danger">Cancelada</Badge></h3>
                )
            default:
                return(
                    <h3 className="text-muted"><Badge pill bg="secondary">Sin membresía</Badge></h3>
                )
        }
    }

    const handleGestionarMembresia = (membresia) => {
        if(usuario?.permisos?.clientes?.membresia){
            setMembresiaActual(membresia);
        }
    }

    useEffect(() => {
        dispatch(limpiarCliente());

        return () => {
            dispatch(limpiarCliente());
        }
    }, []);

    useEffect(() => {
        if(id){
            dispatch(obtenerCliente(id));

            rutasBread[1].path = rutasBread[1].path + id;

            dispatch(editarBreadcrumb(rutasBread));
        }
    }, [id]);

    useEffect(() => {
        if(detalles){
            setClienteData({
                ...clienteData,
                indicadores_generales: detalles.indicadores_generales,
                informacion_personal: {
                    ...detalles.informacion_personal
                },
                citas: detalles.citas,
                paquetes: detalles.contrataciones_paquetes,
                membresias: detalles.contrataciones_membresias,
                pagos: detalles.pagos || []
            });
        }
    }, [detalles]);

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .btn-badge-status{
                            background-color: #fff;
                            border: none;
                            outline: none;
                            padding: 0;
                        }

                        .valor_indicador {
                            font-size: 40px;
                            margin: 0;
                        }

                        .descripcion_indicador {
                            margin: 0;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <NuevoPaquete paquete={nuevoPaquete} reestablecerPaquete={handleAgregarPaquete} />
            <EditarPaquete paquete={editarPaquete} reestablecerPaquete={handleEditarPaquete} />
            <EliminarPaquete paquete={eliminarPaquete} reestablecerPaquete={handleEliminarPaquete} />

            <GestionarMembresia membresia={membresiaActual} reestablecerMembresia={handleGestionarMembresia} />

            {
                usuario?.tipo == 2 ?
                    <>
                        <NuevoPago pago={nuevoPago} reestablecerPago={setNuevoPago} />
                        <EliminarPago pago={eliminarPagoData} reestablecerPago={setEliminarPagoData} />
                    </>
                : null
            }

            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Perfil del cliente</h3>
                    
                    <div className="row mb-4">
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.citas_pendientes, 0, '', '') }</p>
                            <p className="descripcion_indicador">Citas pendientes</p>
                        </div>
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.citas_realizadas, 0, '', '') }</p>
                            <p className="descripcion_indicador">Citas aprobadas</p>
                        </div>
                        <div className="col-md text-center">
                            <p className="valor_indicador">{ formatNumber(indicadores_generales?.citas_canceladas, 0, '', '') }</p>
                            <p className="descripcion_indicador">Citas canceladas</p>
                        </div>
                        {
                            usuario?.tipo == 2 ?
                                <div className="col-md text-center">
                                    <p className="valor_indicador" style={{ color: (indicadores_generales?.saldo || 0) >= 0 ? '#198754' : '#dc3545' }}>{ formatNumber(indicadores_generales?.saldo || 0, 2, '$ ', '') }</p>
                                    <p className="descripcion_indicador">Saldo</p>
                                </div>
                            : null
                        }
                        <div className="col-md text-center">
                            <div className="mt-2 d-flex justify-content-center">
                                <button className="btn-badge-status" onClick={() => handleGestionarMembresia(indicadores_generales?.membresia_actual)}>
                                    { handleStatus(indicadores_generales?.membresia_actual) }
                                </button>
                            </div>
                            <p className="descripcion_indicador">Membresía</p>
                        </div>
                    </div>
                    
                    <div className="row">
                        <div className="col-md-12">
                            <div className="accordion" id="accordionDetalles">
                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingOne">
                                        <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                                            Información de perfil
                                        </button>
                                    </h2>
                                    <div id="collapseOne" className="accordion-collapse collapse show" aria-labelledby="headingOne" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body row">
                                            <div className="col-md-4">
                                                <label className="fw-bold">Nombre</label>
                                                <p>{[informacion_personal?.nombre, informacion_personal?.apellido_paterno, informacion_personal?.apellido_materno].join(' ')}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Celular</label>
                                                <p>{informacion_personal?.celular || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Email</label>
                                                <p>{informacion_personal?.email || 'Sin definir'}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Fecha de nacimiento</label>
                                                <p>{new Date(informacion_personal?.nacimiento).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="fw-bold">Fecha de registro</label>
                                                <p>{new Date(informacion_personal?.creacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingTwo">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="true" aria-controls="collapseTwo">
                                            Citas
                                        </button>
                                    </h2>
                                    <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={citasColumnas}
                                                data={citas}
                                                noDataComponent="No hay citas registradas"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingThree">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="true" aria-controls="collapseThree">
                                            Historial de paquetes
                                        </button>
                                    </h2>
                                    <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingThree" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            {
                                                usuario?.permisos?.clientes?.membresia && 
                                                    <div className="col-md-12 text-end mb-3">
                                                        <div className="btn-group" role="group" aria-label="Button group with nested dropdown">
                                                            <button type="button" className="btn btn-primary" onClick={() => handleAgregarPaquete(id)}><i className="fa-solid fa-plus"></i> Contratación de paquete</button>
                                                        </div>
                                                    </div>
                                            }

                                            <DataTable
                                                columns={paquetesColumnas}
                                                data={paquetes}
                                                noDataComponent="No hay contrataciones de paquetes registradas"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingFour">
                                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFour" aria-expanded="true" aria-controls="collapseFour">
                                            Historial de membresias
                                        </button>
                                    </h2>
                                    <div id="collapseFour" className="accordion-collapse collapse" aria-labelledby="headingFour" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={membresiasColumnas}
                                                data={membresias}
                                                noDataComponent="No hay contrataciones de membresias registradas"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingFive">
                                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFive" aria-expanded="true" aria-controls="collapseFive">
                                        Beneficios adquiridos
                                    </button>
                                    </h2>
                                    <div id="collapseFive" className="accordion-collapse collapse" aria-labelledby="headingFive" data-bs-parent="#accordionDetalles">
                                        <div className="accordion-body">
                                            <DataTable
                                                columns={beneficiosColumnas}
                                                data={indicadores_generales?.membresia_actual?.beneficios}
                                                noDataComponent="No hay beneficios disponibles"
                                                pagination
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 20, 50]}
                                                paginationComponentOptions={paginationOptions}
                                                highlightOnHover={true}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {
                                    usuario?.tipo == 2 ?
                                        <div className="accordion-item">
                                            <h2 className="accordion-header" id="headingSix">
                                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseSix" aria-expanded="true" aria-controls="collapseSix">
                                                Saldo y movimientos
                                            </button>
                                            </h2>
                                            <div id="collapseSix" className="accordion-collapse collapse" aria-labelledby="headingSix" data-bs-parent="#accordionDetalles">
                                                <div className="accordion-body">
                                                    <div className="col-md-12 text-end mb-3">
                                                        <button type="button" className="btn btn-primary" onClick={() => handleAgregarPago(id)}><i className="fa-solid fa-plus"></i> Agregar abono</button>
                                                    </div>
                                                    <DataTable
                                                        columns={pagosColumnas}
                                                        data={pagos}
                                                        noDataComponent="No hay movimientos registrados"
                                                        pagination
                                                        paginationPerPage={10}
                                                        paginationRowsPerPageOptions={[10, 20, 50]}
                                                        paginationComponentOptions={paginationOptions}
                                                        highlightOnHover={true}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    : null
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default DetallesCliente;